import React, { useEffect, useState } from 'react';
import { Card, Table, Spinner, Badge } from 'react-bootstrap';
import { useGetUndistributedLeadsMutation } from '../../api/usersAPISlices';
import CustomPagination from '../utility/CustomPagination';

const UndistributedLeads = () => {
    const [leads, setLeads] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [getUndistributedLeads] = useGetUndistributedLeadsMutation();
    
    const token = JSON.parse(localStorage.getItem("token"));
    const itemsPerPage = 5; // Show 5 items per page

    useEffect(() => {
        fetchUndistributedLeads();
    }, []);

    // Calculate paginated data
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentLeads = leads.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(leads.length / itemsPerPage);

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const fetchUndistributedLeads = async () => {
        setIsLoading(true);
        try {
            const response = await getUndistributedLeads({ token }).unwrap();
            setLeads(response || []);
        } catch (error) {
            console.error('Error fetching undistributed leads:', error);
            setLeads([]);
        } finally {
            setIsLoading(false);
        }
    };

    const truncateText = (text, maxLength = 20) => {
        if (!text) return '-';
        if (text.length <= maxLength) return text;
        return text.substring(0, maxLength) + '...';
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        
        // Convert to California timezone
        const californiaOptions = {
            timeZone: 'America/Los_Angeles',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        };
        
        // Get California date/time components
        const californiaDate = new Date(date.toLocaleString('en-US', { timeZone: 'America/Los_Angeles' }));
        const now = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Los_Angeles' }));
        
        // Format time with AM/PM in California timezone
        const timeString = date.toLocaleTimeString('en-US', californiaOptions);
        
        // Check if today in California timezone
        const isToday = californiaDate.toDateString() === now.toDateString();
        
        // Check if yesterday in California timezone
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        const isYesterday = californiaDate.toDateString() === yesterday.toDateString();
        
        if (isToday) {
            return `Today ${timeString}`;
        } else if (isYesterday) {
            return `Yesterday ${timeString}`;
        } else {
            const dateString = californiaDate.toLocaleDateString('en-US', { 
                month: 'short', 
                day: 'numeric'
            });
            return `${dateString} ${timeString}`;
        }
    };

    const capitalizeCounty = (county) => {
        if (!county) return '-';
        return county
            .toLowerCase()
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };

    const getStateColor = (state) => {
        const colors = {
            'CA': 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', // Orange
            'TX': 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)', // Red
            'FL': 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)', // Cyan
            'NY': 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)', // Indigo
            'PA': 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)', // Purple
            'IL': 'linear-gradient(135deg, #10b981 0%, #059669 100%)', // Green
            'OH': 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)', // Deep Orange
            'GA': 'linear-gradient(135deg, #ec4899 0%, #db2777 100%)', // Pink
            'NC': 'linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)', // Teal
            'MI': 'linear-gradient(135deg, #84cc16 0%, #65a30d 100%)', // Lime
            'NJ': 'linear-gradient(135deg, #a855f7 0%, #9333ea 100%)', // Violet
            'VA': 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', // Blue
            'WA': 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)', // Bright Green
            'AZ': 'linear-gradient(135deg, #facc15 0%, #eab308 100%)', // Yellow
            'MA': 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)', // Sky
            'TN': 'linear-gradient(135deg, #f43f5e 0%, #e11d48 100%)', // Rose
            'IN': 'linear-gradient(135deg, #fb923c 0%, #f97316 100%)', // Light Orange
            'MO': 'linear-gradient(135deg, #818cf8 0%, #6366f1 100%)', // Light Indigo
            'MD': 'linear-gradient(135deg, #c084fc 0%, #a855f7 100%)', // Light Purple
            'WI': 'linear-gradient(135deg, #34d399 0%, #10b981 100%)', // Emerald
        };
        return colors[state] || 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)'; // Gray default
    };

    return (
        <Card style={{ borderRadius: '15px', border: 'none', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)' }}>
            <Card.Body className="p-4">
                <div className="mb-4">
                    <div className="d-flex justify-content-between align-items-start">
                        <div>
                            <h5 className="font-weight-bold mb-1" style={{color: '#1a202c', fontSize: '18px'}}>Undistributed EFX Leads</h5>
                            <p className="text-muted small mb-0" style={{fontSize: '13px'}}>Recent leads from source 1 that weren't distributed</p>
                        </div>
                        {!isLoading && leads.length > 0 && (
                            <div className="text-end">
                                <span className="badge bg-secondary" style={{padding: '6px 12px', fontSize: '12px'}}>
                                    Showing {indexOfFirstItem + 1}-{Math.min(indexOfLastItem, leads.length)} of {leads.length}
                                </span>
                            </div>
                        )}
                    </div>
                </div>

                {isLoading ? (
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
                        <Spinner animation="border" variant="primary" size="sm">
                            <span className="visually-hidden">Loading...</span>
                        </Spinner>
                    </div>
                ) : leads.length > 0 ? (
                    <div className="table-responsive">
                        <Table hover className="mb-0" style={{fontSize: '14px'}}>
                            <thead style={{backgroundColor: '#f8f9fa'}}>
                                <tr>
                                    <th style={{ borderTop: 'none', color: '#6b7280', fontWeight: '600', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '12px 8px' }}>Name</th>
                                    <th style={{ borderTop: 'none', color: '#6b7280', fontWeight: '600', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '12px 8px' }}>Address</th>
                                    <th style={{ borderTop: 'none', color: '#6b7280', fontWeight: '600', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '12px 8px' }}>City</th>
                                    <th style={{ borderTop: 'none', color: '#6b7280', fontWeight: '600', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '12px 8px' }}>State</th>
                                    <th style={{ borderTop: 'none', color: '#6b7280', fontWeight: '600', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '12px 8px' }}>Zip</th>
                                    <th style={{ borderTop: 'none', color: '#6b7280', fontWeight: '600', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '12px 8px' }}>Type</th>
                                    <th style={{ borderTop: 'none', color: '#6b7280', fontWeight: '600', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '12px 8px' }}>County</th>
                                    <th style={{ borderTop: 'none', color: '#6b7280', fontWeight: '600', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '12px 8px' }}>Created</th>
                                </tr>
                            </thead>
                            <tbody>
                                {currentLeads.map((lead, index) => (
                                    <tr key={lead.id} style={{ borderBottom: index === currentLeads.length - 1 ? 'none' : '1px solid #e5e7eb', transition: 'background-color 0.2s' }}>
                                        <td className="font-weight-bold" style={{padding: '12px 8px', color: '#1f2937'}}>{truncateText(lead.name, 25)}</td>
                                        <td style={{padding: '12px 8px', color: '#4b5563'}}>{truncateText(lead.address, 30)}</td>
                                        <td style={{padding: '12px 8px', color: '#4b5563'}}>{truncateText(lead.city, 15)}</td>
                                        <td style={{padding: '12px 8px'}}>
                                            <Badge style={{
                                                background: getStateColor(lead.state),
                                                border: 'none',
                                                padding: '5px 12px',
                                                fontSize: '12px',
                                                fontWeight: '600',
                                                color: '#ffffff',
                                                display: 'inline-block',
                                                minWidth: '45px',
                                                textAlign: 'center',
                                                letterSpacing: '0.5px'
                                            }}>
                                                {lead.state}
                                            </Badge>
                                        </td>
                                        <td style={{padding: '12px 8px', color: '#4b5563', fontFamily: 'monospace', fontSize: '13px'}}>{lead.zip}</td>
                                        <td style={{padding: '12px 8px'}}>
                                            <span style={{color: '#8b5cf6', fontWeight: '500'}}>
                                                {truncateText(lead.property_type, 15)}
                                            </span>
                                        </td>
                                        <td style={{padding: '12px 8px', color: '#4b5563'}}>{capitalizeCounty(truncateText(lead.county, 15))}</td>
                                        <td style={{padding: '12px 8px', color: '#6b7280', fontSize: '13px'}}>{formatDate(lead.created_at)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                ) : (
                    <div className="text-center py-5">
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="mb-3">
                            <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="#28a745" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M2 17L12 22L22 17" stroke="#28a745" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M2 12L12 17L22 12" stroke="#28a745" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <p className="text-muted mb-0">All EFX leads have been distributed!</p>
                        <p className="text-muted small">Great job on lead distribution.</p>
                    </div>
                )}
                
                {!isLoading && leads.length > itemsPerPage && (
                    <div className="mt-3">
                        <CustomPagination
                            current_page={currentPage}
                            last_page={totalPages}
                            onPageChange={handlePageChange}
                        />
                    </div>
                )}
            </Card.Body>
        </Card>
    );
};

export default UndistributedLeads;